import React, { useEffect, useState } from "react";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { Button, Card, Result, Spin, Typography } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";

const { Text } = Typography;

/**
 * PaymentResult Component
 * Shows payment success/cancel result in a new tab
 * Provides a back button to close the tab and return to billing
 */
const PaymentResult = () => {
  const fetch = useAuthenticatedFetch();
  const [status, setStatus] = useState("loading"); // loading, success, failed, cancelled
  const [message, setMessage] = useState("");
  const [creditsAdded, setCreditsAdded] = useState(0);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    const paymentSuccess = urlParams.get("payment_success");
    const paymentCancelled = urlParams.get("payment_cancelled");

    if (sessionId) {
      // Payment link or checkout session completed - verify with session ID
      verifyPayment(sessionId);
    } else if (paymentSuccess === "true") {
      // Legacy success without session ID - credits will be added via webhook
      setStatus("success");
      setMessage("Payment received! Credits will be added to your account shortly.");
    } else if (paymentCancelled === "true") {
      setStatus("cancelled");
      setMessage("Payment was cancelled. No charges were made.");
    } else {
      setStatus("failed");
      setMessage("Invalid payment session. Please try again.");
    }
  }, []);

  const verifyPayment = async (sessionId) => {
    try {
      const response = await fetch.post("/credits/purchase/verify", { sessionId });

      if (response?.success) {
        setStatus("success");
        setCreditsAdded(response.data.creditsAdded || 0);
        if (response.data.alreadyProcessed) {
          setMessage("Payment already processed! Your credits are available.");
        } else {
          setMessage(response.data.message || "Credits added successfully!");
        }
      } else {
        setStatus("failed");
        setMessage(response?.message || "Payment verification failed. Please contact support.");
      }
    } catch (error) {
      console.error("Payment verification error:", error);
      // Check if it's a 403 (session belongs to different user)
      if (error?.response?.status === 403) {
        setStatus("failed");
        setMessage("This payment session does not belong to your account.");
      } else {
        setStatus("failed");
        setMessage("Payment verification failed. If you were charged, credits will be added via our backup system within a few minutes.");
      }
    }
  };

  const handleBackToBilling = () => {
    // Try to send message to opener window to refresh and navigate
    if (window.opener && !window.opener.closed) {
      try {
        // Navigate opener to billing tab and refresh
        window.opener.location.href = "/app/account?tab=billing&refresh=true";
        // Close this tab
        window.close();
      } catch (e) {
        // Cross-origin restrictions - just redirect in current tab
        window.location.href = "/app/account?tab=billing";
      }
    } else {
      // No opener (tab was opened directly or opener closed)
      // Redirect in current tab
      window.location.href = "/app/account?tab=billing";
    }
  };

  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <Result
            icon={<Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />}
            title="Verifying Payment..."
            subTitle="Please wait while we confirm your payment with Stripe."
          />
        );

      case "success":
        return (
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
            title="Payment Successful!"
            subTitle={
              <div>
                <Text style={{ fontSize: "16px", display: "block", marginBottom: "8px" }}>
                  {message}
                </Text>
                {creditsAdded > 0 && (
                  <Text strong style={{ fontSize: "18px", color: "#1990ff" }}>
                    +{creditsAdded} credits added to your account
                  </Text>
                )}
              </div>
            }
            extra={[
              <Button
                key="back"
                type="primary"
                size="large"
                icon={<ArrowLeftOutlined />}
                onClick={handleBackToBilling}
                style={{ borderRadius: "8px", height: "48px", paddingInline: "32px" }}
              >
                Back to Billing
              </Button>,
            ]}
          />
        );

      case "cancelled":
        return (
          <Result
            status="warning"
            icon={<CloseCircleOutlined style={{ color: "#faad14" }} />}
            title="Payment Cancelled"
            subTitle={message}
            extra={[
              <Button
                key="back"
                type="primary"
                size="large"
                icon={<ArrowLeftOutlined />}
                onClick={handleBackToBilling}
                style={{ borderRadius: "8px", height: "48px", paddingInline: "32px" }}
              >
                Back to Billing
              </Button>,
            ]}
          />
        );

      case "failed":
      default:
        return (
          <Result
            status="error"
            icon={<CloseCircleOutlined style={{ color: "#ff4d4f" }} />}
            title="Payment Failed"
            subTitle={message}
            extra={[
              <Button
                key="back"
                type="primary"
                size="large"
                icon={<ArrowLeftOutlined />}
                onClick={handleBackToBilling}
                style={{ borderRadius: "8px", height: "48px", paddingInline: "32px" }}
              >
                Back to Billing
              </Button>,
            ]}
          />
        );
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)",
        padding: "20px",
      }}
    >
      <Card
        style={{
          maxWidth: "500px",
          width: "100%",
          borderRadius: "16px",
          boxShadow: "0 4px 24px rgba(0, 0, 0, 0.1)",
        }}
        bodyStyle={{ padding: "40px 24px" }}
      >
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <img
            src="/images/logo.png"
            alt="UpResume.ai"
            style={{ height: "40px", marginBottom: "16px" }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>
        {renderContent()}
        <div style={{ textAlign: "center", marginTop: "24px" }}>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Powered by Stripe • Secure Payment Processing
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default PaymentResult;
